import { getQuartzConfig } from 'quartz-config/evaluable';
import { get, set } from 'quartz/globalState/api';
import { getHstc } from 'usage-tracker-core/common/defaultTrackers';
import { isHubSpotDomain } from 'usage-tracker-core/client/browserHelper';
import { TRACKING_CONTEXT } from './state/globalState';
import { INTERNAL_TRACKING_CONTEXT } from './state/internalState';
import { getBaseTrackingClient } from './clients/base';
import { getAnonymousTrackingClient } from './clients/anonymous';
import { createHstcScheduler } from './utils';
import { ALLOWED_UNAUTHED_TYPES } from './constants';
const getClientType = hstc => {
  const quartzConfig = getQuartzConfig();
  if ( /* quartzConfig.auth === 'none' */true) {
    return hstc ? 'base' : 'anonymous';
  }
  return 'base';
};
const createClient = trackerProperties => getClientType(trackerProperties.hstc) === 'base' ? getBaseTrackingClient(trackerProperties) : getAnonymousTrackingClient(trackerProperties);

// For auth "none", the tracker must be switched when HSTC changes
// (e.g. cookie banner acceptance or UTK becoming available/unavailable)
const startHstcWatcher = (trackerProperties, initialClient, onSwitch) => {
  let currentHstc = trackerProperties.hstc;
  let currentClientType = getClientType(currentHstc);
  const clientCache = {
    [currentClientType]: initialClient
  };
  return createHstcScheduler({
    getHstc,
    interval: 200,
    callback: newHstc => {
      if (newHstc !== currentHstc) {
        currentHstc = newHstc;
        const nextType = getClientType(newHstc);
        if (nextType !== currentClientType) {
          var _clientCache$nextType;
          currentClientType = nextType;
          (_clientCache$nextType = clientCache[nextType]) !== null && _clientCache$nextType !== void 0 ? _clientCache$nextType : clientCache[nextType] = createClient(Object.assign({}, trackerProperties, {
            hstc: newHstc
          }));
          onSwitch(clientCache[nextType]);
        }
      }
    }
  });
};
export function initializeTracking(trackerProperties) {
  const quartzConfig = getQuartzConfig();
  const listeners = new Set();
  let currentIntervalNumber;
  const onTrackerSwitch = callback => {
    listeners.add(callback);
  };
  const createAndSetTracker = client => {
    listeners.forEach(cb => cb());
    let tracker = client.createTracker({
      standalone: true,
      isExternalHost: quartzConfig.auth === 'none' && !isHubSpotDomain,
      allowUnauthed: ALLOWED_UNAUTHED_TYPES.includes(quartzConfig.auth),
      trackerName: quartzConfig.tracking && quartzConfig.tracking.trackerName,
      properties: {
        deviceId: client.getDeviceId()
      }
    });

    // If Session Replay is enabled, we wrap the tracker instance with a Proxy that allows us to
    // attach Session Replay properties to the tracked events and ensure that Session Replay is properly initialized
    if ( /* quartzConfig.tracking && quartzConfig.tracking.sessionReplay */false) {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const {
        withSessionReplay
      } = require('usage-tracker-session-replay');
      tracker = withSessionReplay(tracker);
    }
    set(TRACKING_CONTEXT, {
      track: tracker.track,
      onTrackerSwitch
    });
    return tracker;
  };
  const existingInternalState = get(INTERNAL_TRACKING_CONTEXT);
  if (existingInternalState !== undefined) {
    existingInternalState.dispose();
  }
  const initialClient = createClient(trackerProperties);
  const initialTracker = createAndSetTracker(initialClient);

  // Legacy compatibility: register quartz-tracking's tracker as the default primary
  // tracker so shared libraries that still read getPrimaryTracker() keep working.
  // Uses the Map directly (not setPrimaryTracker, which throws on re-registration)
  // so re-init (hot-reload, test re-mount) replaces the stale entry instead of silently
  // keeping a disposed tracker.
  if ( /* quartzConfig.tracking && quartzConfig.tracking.provideLegacyDefaultPrimaryTracker */false) {
    const {
      primaryTrackers
    } =
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require('usage-tracker-container/primaryTrackerState');
    primaryTrackers.set('default', initialTracker);
  }
  if ( /* quartzConfig.auth === 'none' */true) {
    currentIntervalNumber = startHstcWatcher(trackerProperties, initialClient, createAndSetTracker);
  }
  const disposeTracking = () => {
    clearInterval(currentIntervalNumber);
    listeners.forEach(listener => listeners.delete(listener));
    set(TRACKING_CONTEXT, {
      track: () => {},
      onTrackerSwitch: () => {}
    });
  };
  set(INTERNAL_TRACKING_CONTEXT, {
    dispose: disposeTracking
  });
}